import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import {
  FileSignature,
  CheckCircle2,
  AlertCircle,
  Loader2,
  PenLine,
  RotateCcw,
  Check,
  ChevronLeft,
  ChevronRight,
  Users,
  Play,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL + '/api/hr';

export default function PublicSign() {
  const { documentId, token } = useParams();
  const sigCanvas = useRef(null);

  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [signer, setSigner] = useState(null);
  const [allSigners, setAllSigners] = useState([]);
  
  // Flow state
  const [started, setStarted] = useState(false);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  
  // PDF state
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 });

  useEffect(() => {
    fetchDocument();
  }, [documentId, token]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API}/sign/${documentId}/${token}`);
      
      if (response.data.already_signed) {
        setAlreadySigned(true);
        setLoading(false);
        return;
      }
      
      setDocumentData(response.data.document);
      setSigner(response.data.signer);
      setAllSigners(response.data.all_signers || []);
      
      // Set initial page to first signature field
      if (response.data.signer?.signature_fields?.[0]) {
        setCurrentPage(response.data.signer.signature_fields[0].page);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    setStarted(true);
    // Navigate to first signature field
    if (signer?.signature_fields?.[0]) {
      setCurrentPage(signer.signature_fields[0].page);
    }
  };

  const currentField = signer?.signature_fields?.[currentFieldIndex];

  const handleSignHere = () => {
    setShowSignModal(true);
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.toDataURL('image/png');
      setSignatureData(dataUrl);
      setShowSignModal(false);
    } else {
      toast.error('Please draw your signature');
    }
  };

  const handleNextField = () => {
    if (currentFieldIndex < (signer?.signature_fields?.length || 0) - 1) {
      const nextIndex = currentFieldIndex + 1;
      setCurrentFieldIndex(nextIndex);
      setCurrentPage(signer.signature_fields[nextIndex].page);
    }
  };

  const handleFinish = async () => {
    if (!signatureData) {
      toast.error('Please sign at your designated location');
      return;
    }

    setSigning(true);
    try {
      const response = await axios.post(`${API}/sign/${documentId}/${token}`, {
        signature: signatureData
      });

      setCompleted(true);
      toast.success('Document signed successfully!');

      if (response.data.all_signed) {
        toast.success('All signatures collected!');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error(err.response?.data?.detail || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    setPageSize({ width: page.width, height: page.height });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto" />
          <p className="mt-4 text-slate-600">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Unable to Load</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  // Already signed state
  if (alreadySigned) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Already Signed</h1>
          <p className="text-slate-600">You have already signed this document.</p>
        </div>
      </div>
    );
  }

  // Completed state
  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Signing Complete!</h1>
          <p className="text-slate-600 mb-6">
            Thank you, {signer?.name}. Your signature has been recorded.
          </p>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">Document</p>
            <p className="font-medium text-slate-900">{documentData?.title}</p>
          </div>
          <p className="text-sm text-slate-500 mt-6">
            You will receive a copy of the signed document via email once all parties have signed.
          </p>
        </div>
      </div>
    );
  }

  // Start screen
  if (!started) {
    return (
      <div className="min-h-screen bg-slate-100" data-testid="public-sign-page">
        {/* Header */}
        <div className="bg-slate-900 text-white py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSignature className="h-6 w-6 text-emerald-400" />
              <span className="font-bold text-xl">MyHR Orbit</span>
            </div>
            <span className="text-sm text-slate-400">Secure Document Signing</span>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Document Info */}
            <div className="p-8 text-center border-b border-slate-100">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {documentData?.title}
              </h1>
              <p className="text-slate-500">
                {documentData?.document_type?.replace('_', ' ').toUpperCase()}
              </p>
            </div>

            {/* Signer Info */}
            <div className="p-6 bg-slate-50">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: signer?.color || '#10b981' }}
                >
                  {signer?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{signer?.name}</p>
                  <p className="text-sm text-slate-500">{signer?.email}</p>
                </div>
              </div>
            </div>

            {/* Message */}
            {documentData?.message && (
              <div className="p-6 border-t border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Message from sender:</p>
                <p className="text-slate-700">{documentData.message}</p>
              </div>
            )}

            {/* Signature Fields Info */}
            <div className="p-6 border-t border-slate-100">
              <div className="flex items-center gap-3 text-slate-600">
                <PenLine className="h-5 w-5" />
                <span>
                  You have <strong>{signer?.signature_fields?.length || 0}</strong> signature field(s) to complete
                </span>
              </div>
            </div>

            {/* Other Signers */}
            {allSigners.length > 1 && (
              <div className="p-6 border-t border-slate-100">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                  <Users className="h-4 w-4" />
                  <span>Other signers</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allSigners.filter(s => s.email !== signer?.email).map((s, idx) => (
                    <span
                      key={idx}
                      className={`px-3 py-1 rounded-full text-sm ${
                        s.status === 'signed'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {s.name} {s.status === 'signed' && '✓'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Start Button */}
            <div className="p-6 border-t border-slate-100">
              <Button
                onClick={handleStart}
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
                data-testid="start-signing-btn"
              >
                <Play className="h-5 w-5 mr-2" />
                Start Signing
              </Button>
              <p className="text-center text-sm text-slate-500 mt-4">
                By clicking Start, you agree that your electronic signature is legally binding.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Signing flow
  const displayWidth = Math.min(750, window.innerWidth - 50);
  const totalFields = signer?.signature_fields?.length || 0;
  const isLastField = currentFieldIndex >= totalFields - 1;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="signing-flow">
      {/* Header */}
      <div className="bg-slate-900 text-white py-3 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSignature className="h-5 w-5 text-emerald-400" />
            <span className="font-bold">MyHR Orbit</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400">
              Signing as <span className="text-white font-medium">{signer?.name}</span>
            </span>
            <span className="px-2 py-1 bg-emerald-600 rounded text-xs font-medium">
              {currentFieldIndex + 1} of {totalFields}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* PDF Viewer */}
        <div className="flex-1 flex flex-col">
          {/* Page Nav */}
          <div className="flex items-center justify-center gap-4 p-3 bg-white border-b">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium">Page {currentPage} / {numPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
              disabled={currentPage >= numPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* PDF */}
          <div className="flex-1 overflow-auto p-4 flex justify-center bg-slate-200">
            <div className="relative bg-white shadow-xl">
              {documentData?.file_url && (
                <Document
                  file={`${process.env.REACT_APP_BACKEND_URL}${documentData.file_url}`}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<Loader2 className="h-8 w-8 animate-spin m-8" />}
                >
                  <Page
                    pageNumber={currentPage}
                    onLoadSuccess={onPageLoadSuccess}
                    width={displayWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              )}

              {/* Signature Field Overlay */}
              {currentField && currentField.page === currentPage && (
                <div
                  className={`absolute border-2 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
                    signatureData 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-dashed border-blue-500 bg-blue-50 animate-pulse'
                  }`}
                  style={{
                    left: `${currentField.x - currentField.width/2}%`,
                    top: `${currentField.y - currentField.height/2}%`,
                    width: `${currentField.width}%`,
                    height: `${currentField.height}%`
                  }}
                  onClick={handleSignHere}
                >
                  {signatureData ? (
                    <img
                      src={signatureData}
                      alt="Your signature"
                      className="max-w-full max-h-full object-contain p-1"
                    />
                  ) : (
                    <span className="text-blue-600 font-medium text-sm px-2 text-center">
                      Click to sign here
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-white border-l border-slate-200 p-6 flex flex-col">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">
            {signatureData ? 'Signature Added' : 'Sign Here'}
          </h3>

          {/* Current field info */}
          <div className="p-4 bg-slate-50 rounded-lg mb-4">
            <p className="text-sm text-slate-500">Signature field {currentFieldIndex + 1} of {totalFields}</p>
            <p className="text-sm text-slate-500 mt-1">Page {currentField?.page}</p>
          </div>

          {/* Sign Button / Signature Preview */}
          {signatureData ? (
            <div className="space-y-4">
              <div
                className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50"
              >
                <img
                  src={signatureData}
                  alt="Your signature"
                  className="max-h-20 mx-auto"
                />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSignatureData(null);
                  setShowSignModal(true);
                }}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Redo Signature
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleSignHere}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
              data-testid="sign-here-btn"
            >
              <PenLine className="h-5 w-5 mr-2" />
              Sign Here
            </Button>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Navigation */}
          <div className="space-y-3 pt-4 border-t border-slate-200">
            {!isLastField ? (
              <Button
                onClick={handleNextField}
                disabled={!signatureData}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
              >
                Next Field
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleFinish}
                disabled={!signatureData || signing}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                data-testid="finish-signing-btn"
              >
                {signing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    Finish & Submit
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5 text-emerald-500" />
              Draw Your Signature
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Use your mouse or finger to draw your signature below.
            </p>

            <div className="border-2 border-slate-300 rounded-lg bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                  width: 450,
                  height: 200,
                  className: 'w-full rounded-lg'
                }}
              />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={clearSignature} className="flex-1">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
              <Button
                onClick={saveSignature}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="adopt-signature-btn"
              >
                <Check className="h-4 w-4 mr-2" />
                Adopt Signature
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// FileText component (in case it's missing)
function FileText({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
